// The prerendered <head> (scripts/prerender-og.mjs) and react-helmet's used to
// both stay in the document. Helmet only replaces tags it created, so a hydrated
// page carried two descriptions, two canonicals and two sets of og:* tags — and a
// URL served from the host's fallback file kept the homepage's canonical next to
// its own. The prerender now marks its tags, and this hands each one over as
// soon as Helmet has rendered a tag of the same kind:
//   data-prerender="page" — the value belongs to the URL the file was built for
//   data-prerender="site" — a sitewide social fallback from index.html
// A marked tag the page never sets for itself stays (twitter:title on the info
// pages, the product price tags, the default share image).
//
// Once the served tags no longer describe the URL (an in-app navigation, or the
// host's fallback file), only Helmet's are left, and most pages render no share
// image and no twitter:title of their own. Those are filled in here:
//   data-prerender="fill" — the sitewide image, or a twitter:* copy of og:*

const MARK = "data-prerender";

// index.html's og:image, which a prerendered page may have replaced with its own.
const SITE_SHARE_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/6yebqbVNpAUffaj0O2qBtV2ri0t1/social-images/social-1771291854750-image543.webp";

/** [attribute, key, the og: tag it copies; none = the sitewide image]. og:image first: twitter:image copies it. */
const FILL: [string, string, string?][] = [
  ["property", "og:image"],
  ["name", "twitter:image", "og:image"],
  ["name", "twitter:title", "og:title"],
  ["name", "twitter:description", "og:description"],
];

const trimSlash = (pathname: string) => pathname.replace(/\/+$/, "") || "/";

/** "meta:description", "meta:og:title", "link:canonical" … */
const kind = (el: Element) =>
  el.tagName === "LINK" ? `link:${el.getAttribute("rel")}` : `meta:${el.getAttribute("name") ?? el.getAttribute("property")}`;

export function adoptPrerenderedHead(): void {
  const head = document.head;
  const marked = () => Array.from(head.querySelectorAll(`[${MARK}]`));
  if (!marked().length) return; // dev server, or a build without the prerender step

  const dropPageTags = () => head.querySelectorAll(`[${MARK}="page"]`).forEach((el) => el.remove());

  // dist/index.html — the homepage — is also what the host serves for every path
  // it has no file for: a product added since the last build, /search, a
  // mistyped URL. There the homepage's canonical, description and og:url describe
  // another page, so they go before React renders anything.
  const servedPath = location.pathname;
  let builtPath: string | null = null;
  try {
    const builtFor = head.querySelector(`meta[property="og:url"][${MARK}]`)?.getAttribute("content");
    builtPath = builtFor ? new URL(builtFor).pathname : null;
  } catch {
    builtPath = null;
  }
  // True once the served page tags are gone: from then on the head is Helmet's alone.
  let adrift = builtPath === null || trimSlash(builtPath) !== trimSlash(servedPath);
  if (adrift) dropPageTags();

  const fill = () => {
    for (const [attr, key, from] of FILL) {
      const own = head.querySelector(`meta[${attr}="${key}"]:not([${MARK}="fill"])`);
      const filled = head.querySelector(`meta[${attr}="${key}"][${MARK}="fill"]`);
      const value = own ? null : from ? head.querySelector(`meta[property="${from}"]`)?.getAttribute("content") : SITE_SHARE_IMAGE;
      if (!value) {
        filled?.remove();
        continue;
      }
      const el = filled ?? document.createElement("meta");
      if (el.getAttribute("content") !== value) el.setAttribute("content", value);
      if (!filled) {
        el.setAttribute(attr, key);
        el.setAttribute(MARK, "fill");
        head.appendChild(el);
      }
    }
  };

  // Helmet commits its tags in an animation frame after React renders, and again
  // on every route change; each commit adds or removes children of <head>.
  const observer = new MutationObserver(() => {
    // Client-side navigation: nothing written for the first URL applies any more.
    if (location.pathname !== servedPath) {
      adrift = true;
      dropPageTags();
    }
    const helmetTags = Array.from(head.querySelectorAll("meta[data-rh], link[data-rh]"));
    const rendered = new Set(helmetTags.map(kind));
    // A page that asks not to be indexed renders no canonical of its own
    // (src/components/OGTags.tsx), so the prerendered one must not outlive it.
    const noindex = helmetTags.some(
      (el) => el.getAttribute("name") === "robots" && /noindex/i.test(el.getAttribute("content") ?? ""),
    );
    for (const el of marked()) {
      const k = kind(el);
      if (rendered.has(k) || (noindex && k === "link:canonical")) el.remove();
    }
    // Stays connected: the next navigation needs the fill again. Its own
    // changes call it back once more, and that pass changes nothing.
    if (adrift) fill();
  });
  observer.observe(head, { childList: true });
}
